import { useState } from 'react';
import { toast } from '@/hooks/use-toast';
import { useUpdateProductVariant } from '@/services/product-variant-service';
import { ProductVariant } from '@/types/product-type';
import { ProductVariantUpdateFormValues } from '@/types/product-variant-type';

interface UseVariantEditOptions {
    productId: string;
    onSuccess?: () => void;
}

export function useVariantEdit({ productId, onSuccess }: UseVariantEditOptions) {
    const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
    const [editingVariant, setEditingVariant] = useState<ProductVariant | null>(null);
    const [isUpdating, setIsUpdating] = useState(false);

    const updateMutation = useUpdateProductVariant();

    const handleEdit = (variant: ProductVariant) => {
        setEditingVariant(variant);
        setIsEditDialogOpen(true);
    };

    const handleEditSubmit = async (data: ProductVariantUpdateFormValues) => {
        if (!editingVariant || !productId) return;

        setIsUpdating(true);
        try {
            const response = await updateMutation.executeAsync({
                id: editingVariant.id,
                data,
                productId,
            });

            if (response.success) {
                toast({
                    title: 'Success',
                    description: 'Variant updated successfully',
                });
                setIsEditDialogOpen(false);
                setEditingVariant(null);
                onSuccess?.();
            } else {
                toast({
                    title: 'Error',
                    description: response.message || 'Failed to update variant',
                    variant: 'destructive',
                });
            }
        } catch (error) {
            toast({
                title: 'Error',
                description: 'Failed to update variant',
                variant: 'destructive',
            });
        } finally {
            setIsUpdating(false);
        }
    };

    const handleCancel = () => {
        setIsEditDialogOpen(false);
        setEditingVariant(null);
    };

    return {
        // State
        isEditDialogOpen,
        setIsEditDialogOpen,
        editingVariant,
        setEditingVariant,
        isUpdating,

        // Actions
        handleEdit,
        handleEditSubmit,
        handleCancel,
    };
}
